/**
 * Tâche 1285 (Eric 2026-06-30) — Compte rendu de « Générer dictionnaire ».
 *
 * Le bouton ne disait que des chiffres dans un toast : impossible de savoir
 * CE QUI est entré au dictionnaire. Ici, les termes détectés sont nommés :
 *   - en BLEU les nouveaux termes, avec leur définition ;
 *   - en VERT les termes enrichis, avec le texte effectivement ajouté.
 */
import { useState } from "react";
import { createPortal } from "react-dom";

// Les appels réseau vivent dans SynthesisPanel (`onSearchEnrichments`,
// `onApplyEnrichments`) : cette fenêtre reste purement d'affichage, donc
// testable au rendu réel sans harnais réseau.
export default function DictionaryGenerationModal({
  open, onClose, result, lang = "fr", onSearchEnrichments, onApplyEnrichments,
}) {
  // Tâche 1285 (Eric 2026-06-30) — Deuxième temps : enrichir les termes DÉJÀ
  // au dictionnaire. La recherche part sur un bouton, jamais à l'ouverture :
  // c'est un appel IA, donc une dépense que l'utilisateur doit décider.
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [enrichReason, setEnrichReason] = useState("");
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState([]);

  const chercherEnrichissements = async () => {
    if (!onSearchEnrichments || enrichLoading) return;
    setEnrichLoading(true);
    try {
      const data = await onSearchEnrichments();
      setProposals(data?.proposals || []);
      setEnrichReason(data?.reason || "");
      // Rien n'est coché d'office : on ne modifie pas un dictionnaire par
      // inadvertance.
      setChecked({});
    } catch (_) {
      // Le message d'erreur est affiché par SynthesisPanel (toast).
    } finally {
      setEnrichLoading(false);
    }
  };

  const appliquer = async () => {
    const ids = Object.keys(checked).filter((k) => checked[k]);
    if (!ids.length || applying || !onApplyEnrichments) return;
    setApplying(true);
    try {
      const data = await onApplyEnrichments(ids);
      setApplied(data?.applied || []);
      setProposals((prev) => (prev || []).filter((p) => !ids.includes(p.entry_id)));
      setChecked({});
    } catch (_) {
      // Le message d'erreur est affiché par SynthesisPanel (toast).
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  const created = result?.created_details || [];
  const enriched = result?.enriched_details || [];
  const nothing = created.length === 0 && enriched.length === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="dictionary-generation-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <span aria-hidden="true">📘</span>
            {lang === "fr" ? "Termes détectés dans la synthèse" : "Terms detected in the synthesis"}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="dictionary-generation-close-btn"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-5">
          {nothing && (
            <div className="py-10 text-center text-sm text-zinc-600" data-testid="dictionary-generation-empty">
              {lang === "fr"
                ? "Aucun nouveau terme détecté — le dictionnaire couvre déjà le vocabulaire de cette synthèse."
                : "No new term detected — the dictionary already covers this synthesis."}
            </div>
          )}

          {created.length > 0 && (
            <section data-testid="dictionary-generation-created">
              <h3 className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-2">
                {lang === "fr"
                  ? `${created.length} terme(s) ajouté(s)`
                  : `${created.length} term(s) added`}
              </h3>
              <ul className="space-y-2">
                {created.map((c) => (
                  <li
                    key={c.id || c.term}
                    className="rounded border border-blue-300 bg-blue-50/70 p-3"
                    data-testid={`dictionary-created-${c.term}`}
                  >
                    <div className="text-xs font-bold text-blue-900">{c.term}</div>
                    <div className="mt-1 text-[11px] text-blue-900/90 whitespace-pre-wrap break-words">
                      {c.definition}
                    </div>
                    {c.duplicate_of_term && (
                      <div className="mt-1.5 text-[11px] font-medium text-amber-800">
                        {lang === "fr"
                          ? `Doublon potentiel avec « ${c.duplicate_of_term} » — à trancher dans le Dictionnaire.`
                          : `Possible duplicate of "${c.duplicate_of_term}".`}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {enriched.length > 0 && (
            <section data-testid="dictionary-generation-enriched">
              <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">
                {lang === "fr"
                  ? `${enriched.length} terme(s) mis à jour`
                  : `${enriched.length} term(s) updated`}
              </h3>
              <ul className="space-y-2">
                {enriched.map((e) => (
                  <li
                    key={e.id || e.term}
                    className="rounded border border-emerald-300 bg-emerald-50/70 p-3"
                    data-testid={`dictionary-enriched-${e.term}`}
                  >
                    <div className="text-xs font-bold text-emerald-900">{e.term}</div>
                    <div className="mt-1 text-[11px] text-emerald-900/90 whitespace-pre-wrap break-words">
                      {e.added_text
                        || (lang === "fr"
                          ? "Cette synthèse a été ajoutée aux sources du terme (définition inchangée)."
                          : "This synthesis was added to the term's sources (definition unchanged).")}
                    </div>
                    {(e.aliases_added || []).length > 0 && (
                      <div className="mt-1.5 text-[11px] text-emerald-800">
                        {lang === "fr" ? "Synonymes ajoutés : " : "Aliases added: "}
                        {(e.aliases_added || []).join(", ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Tâche 1285 — Deuxième temps : les termes DÉJÀ au dictionnaire que
              ce document précise. Liste à cocher avec l'avant et l'après :
              personne ne doit valider une définition qu'il n'a pas lue. */}
          <section className="border-t border-zinc-200 pt-4" data-testid="dictionary-enrichment-section">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-700">
                  {lang === "fr" ? "Enrichir des termes déjà définis" : "Enrich existing terms"}
                </h3>
                <p className="text-[11px] text-zinc-500 mt-1 max-w-xl">
                  {lang === "fr"
                    ? "Compare ce document aux définitions déjà en place. Un appel IA est nécessaire, donc facturé : c'est vous qui le déclenchez."
                    : "Compares this document with existing definitions. One AI call is needed, so you trigger it."}
                </p>
              </div>
              <button
                onClick={chercherEnrichissements}
                disabled={enrichLoading || !onSearchEnrichments}
                className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-bold text-zinc-800 whitespace-nowrap disabled:opacity-50"
                data-testid="dictionary-enrichment-search-btn"
              >
                {enrichLoading
                  ? (lang === "fr" ? "Recherche…" : "Searching…")
                  : (lang === "fr" ? "Chercher des enrichissements" : "Search enrichments")}
              </button>
            </div>

            {proposals !== null && proposals.length === 0 && (
              <p className="mt-3 text-[11px] text-zinc-600" data-testid="dictionary-enrichment-empty">
                {enrichReason || (lang === "fr"
                  ? "Ce document ne précise aucun des termes déjà définis."
                  : "This document does not refine any existing term.")}
              </p>
            )}

            {proposals !== null && proposals.length > 0 && (
              <>
                <ul className="mt-3 space-y-2">
                  {proposals.map((p) => (
                    <li
                      key={p.entry_id}
                      className="rounded border border-zinc-300 bg-zinc-50 p-3"
                      data-testid={`dictionary-enrichment-proposal-${p.term}`}
                    >
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!checked[p.entry_id]}
                          onChange={(e) => setChecked((prev) => ({ ...prev, [p.entry_id]: e.target.checked }))}
                          className="mt-0.5 h-3.5 w-3.5 accent-emerald-600"
                          data-testid={`dictionary-enrichment-check-${p.entry_id}`}
                        />
                        <span className="text-xs font-bold text-zinc-900">{p.term}</span>
                      </label>
                      {p.reason && (
                        <p className="mt-1 text-[11px] text-zinc-600 pl-6">{p.reason}</p>
                      )}
                      <div className="mt-2 pl-6 grid gap-2 md:grid-cols-2">
                        <div className="rounded border border-zinc-200 bg-white p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                            {lang === "fr" ? "Avant" : "Before"}
                          </div>
                          <div className="mt-1 text-[11px] text-zinc-700 whitespace-pre-wrap break-words">
                            {p.definition_before || (lang === "fr" ? "(aucune définition)" : "(no definition)")}
                          </div>
                        </div>
                        <div className="rounded border border-emerald-300 bg-emerald-50/70 p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                            {lang === "fr" ? "Après" : "After"}
                          </div>
                          <div
                            className="mt-1 text-[11px] text-emerald-900 whitespace-pre-wrap break-words"
                            data-testid={`dictionary-enrichment-after-${p.entry_id}`}
                          >
                            {p.definition_after}
                          </div>
                        </div>
                      </div>
                      <p className="mt-1.5 pl-6 text-[10px] text-zinc-500">
                        {p.already_source
                          ? (lang === "fr" ? "Ce document figure déjà dans les sources du terme." : "Already a source of this term.")
                          : (lang === "fr" ? "Ce document sera ajouté aux sources du terme." : "This document will be added to the term's sources.")}
                      </p>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={appliquer}
                    disabled={applying || !Object.values(checked).some(Boolean)}
                    className="px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold disabled:opacity-50"
                    data-testid="dictionary-enrichment-apply-btn"
                  >
                    {applying
                      ? (lang === "fr" ? "Enrichissement…" : "Enriching…")
                      : (lang === "fr" ? "Enrichir les termes cochés" : "Enrich checked terms")}
                  </button>
                  <span className="text-[10px] text-zinc-500">
                    {lang === "fr"
                      ? "L'ancienne définition reste consultable dans l'historique du terme."
                      : "The previous definition stays available in the term's history."}
                  </span>
                </div>
              </>
            )}

            {applied.length > 0 && (
              <p className="mt-3 text-[11px] font-medium text-emerald-800" data-testid="dictionary-enrichment-applied">
                {lang === "fr"
                  ? `Enrichi : ${applied.map((a) => a.term).join(", ")}.`
                  : `Enriched: ${applied.map((a) => a.term).join(", ")}.`}
              </p>
            )}
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-zinc-200 text-[11px] text-zinc-600">
          <span data-testid="dictionary-generation-footer">
            {lang === "fr"
              ? `${result?.total_detected ?? 0} terme(s) détecté(s) · ${result?.terms_skipped ?? 0} inchangé(s)`
              : `${result?.total_detected ?? 0} detected · ${result?.terms_skipped ?? 0} unchanged`}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold"
            data-testid="dictionary-generation-ok-btn"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
